import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Room, RoomEvent } from 'livekit-client';
import { VoiceParticipant, UserData } from '../types';
import { MicIcon, VolumeOffIcon, CloseIcon, CameraIcon, ScreenShareIcon, FlipCameraIcon } from './Icons';
import { requestMicrophonePermission, checkSecureContext } from '../utils/permissionUtils';

export interface RemoteStreamViewProps {
    stream: MediaStream | null;
    isLocal?: boolean;
    muted?: boolean;
    className?: string;
    style?: React.CSSProperties;
}

export const RemoteStreamView = React.memo(({ stream, isLocal, muted, className, style }: RemoteStreamViewProps) => {
    const videoRef = useRef<HTMLVideoElement>(null);

    useEffect(() => {
        const video = videoRef.current;
        if (video && stream) {
            if (video.srcObject !== stream) {
                video.srcObject = stream;
            }
            video.play().catch(err => {
                console.warn('[RemoteStreamView] play() failed/prevented:', err);
            });
        }
        return () => {
            if (video) {
                video.srcObject = null;
            }
        };
    }, [stream]);

    return (
        <video
            ref={videoRef}
            autoPlay
            playsInline
            muted={true}
            className={className}
            style={style}
        />
    );
});

export const RemoteAudioPlayer = React.memo(({ stream, muted }: { stream: MediaStream | null; muted?: boolean }) => {
    const audioRef = useRef<HTMLAudioElement>(null);

    useEffect(() => {
        const audio = audioRef.current;
        if (audio && stream) {
            if (audio.srcObject !== stream) {
                audio.srcObject = stream;
            }
            audio.play().catch(err => {
                console.warn('[RemoteAudioPlayer] play() failed/prevented:', err);
            });
        }
        return () => {
            if (audio) {
                audio.srcObject = null;
            }
        };
    }, [stream]);

    return (
        <audio
            ref={audioRef}
            autoPlay
            playsInline
            muted={muted}
            style={{ display: 'none' }}
        />
    );
});

interface VoiceRoomProps {
    communityId: string;
    channelId: string;
    currentUser: string;
    usersData: Record<string, UserData>;
    socket: any;
    themeColor?: string;
    onLeave?: () => void;
    onRemoteStreamsUpdate?: (streams: Record<string, MediaStream>) => void;
    onLocalStreamUpdate?: (stream: MediaStream | null) => void;
    isMuted?: boolean;
    setIsMuted?: (muted: boolean) => void;
    isDeafened?: boolean;
    setIsDeafened?: (deafened: boolean) => void;
}

export const VoiceRoom: React.FC<VoiceRoomProps> = ({ 
    communityId, channelId, currentUser, usersData, socket, 
    themeColor = '#2563eb', onLeave, onRemoteStreamsUpdate, onLocalStreamUpdate,
    isMuted: propIsMuted, setIsMuted: propSetIsMuted,
    isDeafened: propIsDeafened, setIsDeafened: propSetIsDeafened
}) => {
    const [participants, setParticipants] = useState<VoiceParticipant[]>([]);
    const [localStream, setLocalStream] = useState<MediaStream | null>(null);
    const [localMuted, setLocalMuted] = useState(false);
    const [localDeafened, setLocalDeafened] = useState(false);
    const [isCameraOn, setIsCameraOn] = useState(false);
    const [isScreenSharing, setIsScreenSharing] = useState(false);
    const [facingMode, setFacingMode] = useState<'user' | 'environment'>('user');
    const [focusedParticipant, setFocusedParticipant] = useState<VoiceParticipant | null>(null);

    const isMuted = propIsMuted !== undefined ? propIsMuted : localMuted;
    const setIsMuted = propSetIsMuted !== undefined ? propSetIsMuted : setLocalMuted;
    const isDeafened = propIsDeafened !== undefined ? propIsDeafened : localDeafened;
    const setIsDeafened = propSetIsDeafened !== undefined ? propSetIsDeafened : setLocalDeafened;

    const roomRef = useRef<Room | null>(null);
    const [remoteStreamsMap, setRemoteStreamsMap] = useState<Record<string, MediaStream>>({});
    const [micError, setMicError] = useState<string | null>(null);

    const isMutedRef = useRef(isMuted);
    const isDeafenedRef = useRef(isDeafened);
    const isCameraOnRef = useRef(isCameraOn);
    const isScreenSharingRef = useRef(isScreenSharing);
    const participantsRef = useRef(participants);

    const [logs, setLogs] = useState<string[]>([]);
    const addLog = (msg: string) => {
        const timestamp = new Date().toLocaleTimeString();
        console.log(`[VoiceDiagnostics] ${msg}`);
        setLogs(prev => [`[${timestamp}] ${msg}`, ...prev].slice(0, 30));
    };

    useEffect(() => { isMutedRef.current = isMuted; }, [isMuted]);
    useEffect(() => { isDeafenedRef.current = isDeafened; }, [isDeafened]);
    useEffect(() => { isCameraOnRef.current = isCameraOn; }, [isCameraOn]);
    useEffect(() => { isScreenSharingRef.current = isScreenSharing; }, [isScreenSharing]);
    useEffect(() => { participantsRef.current = participants; }, [participants]);

    useEffect(() => {
        if (onLocalStreamUpdate) onLocalStreamUpdate(localStream);
    }, [localStream, onLocalStreamUpdate]);

    useEffect(() => {
        if (onRemoteStreamsUpdate) onRemoteStreamsUpdate(remoteStreamsMap);
    }, [remoteStreamsMap, onRemoteStreamsUpdate]);

    const effectiveMuted = isMuted || isDeafened;

    // Sync remote streams whenever participants change
    useEffect(() => {
        if (!roomRef.current) return;
        
        const newMap: Record<string, MediaStream> = {};
        roomRef.current.remoteParticipants.forEach((participant) => {
            const matchedVoiceUser = participants.find(p => p.username.toLowerCase() === participant.identity.toLowerCase());
            if (!matchedVoiceUser) return;

            const mediaStream = new MediaStream();
            participant.trackPublications.forEach((pub) => {
                const track = pub.track || pub.videoTrack || pub.audioTrack;
                if (track && track.mediaStreamTrack) {
                    mediaStream.addTrack(track.mediaStreamTrack);
                }
            });

            if (mediaStream.getTracks().length > 0) {
                newMap[participant.identity.toLowerCase()] = mediaStream;
            }
        });
        setRemoteStreamsMap(newMap);
    }, [participants]);

    const initLiveKit = async () => {
        try {
            addLog('Requesting microphone permission...');
            await requestMicrophonePermission();
            addLog('Microphone permission step completed.');

            const roomName = `${communityId}:${channelId}`;
            addLog(`Fetching LiveKit token for room: ${roomName}`);
            const res = await fetch(`/api/livekit/token?room=${encodeURIComponent(roomName)}&identity=${encodeURIComponent(currentUser)}`);
            if (!res.ok) throw new Error('Failed to generate LiveKit token');
            const data = await res.json();
            addLog('LiveKit token fetched successfully.');

            const room = new Room({
                adaptiveStream: true,
                dynacast: true
            });
            roomRef.current = room;

            const updateLocalStream = () => {
                const tracks = Array.from(room.localParticipant.trackPublications.values())
                    .map(pub => pub.track)
                    .filter(t => t && t.mediaStreamTrack);
                addLog(`Local streams updated. Found ${tracks.length} active tracks.`);
                if (tracks.length > 0) {
                    const stream = new MediaStream();
                    tracks.forEach(t => {
                        if (t?.mediaStreamTrack) stream.addTrack(t.mediaStreamTrack);
                    });
                    setLocalStream(stream);
                } else {
                    setLocalStream(null);
                }
            };

            const updateRemoteStreams = () => {
                const newMap: Record<string, MediaStream> = {};
                room.remoteParticipants.forEach((participant) => {
                    const mediaStream = new MediaStream();
                    participant.trackPublications.forEach((pub) => {
                        const track = pub.track || pub.videoTrack || pub.audioTrack;
                        if (track && track.mediaStreamTrack) {
                            mediaStream.addTrack(track.mediaStreamTrack);
                        }
                    });

                    if (mediaStream.getTracks().length > 0) {
                        newMap[participant.identity.toLowerCase()] = mediaStream;
                    }
                });
                setRemoteStreamsMap(newMap);
            };

            room.on(RoomEvent.LocalTrackPublished, updateLocalStream);
            room.on(RoomEvent.LocalTrackUnpublished, updateLocalStream);
            room.on(RoomEvent.TrackSubscribed, (track, pub, participant) => {
                addLog(`Subscribed to track ${track.sid} (${track.kind}) from ${participant.identity}`);
                updateRemoteStreams();
            });
            room.on(RoomEvent.TrackUnsubscribed, (track, pub, participant) => {
                addLog(`Unsubscribed from track ${track.sid} (${track.kind}) from ${participant.identity}`);
                updateRemoteStreams();
            });
            room.on(RoomEvent.ParticipantConnected, (p) => {
                addLog(`Remote participant joined LiveKit: ${p.identity}`);
                updateRemoteStreams();
            });
            room.on(RoomEvent.ParticipantDisconnected, (p) => {
                addLog(`Remote participant left LiveKit: ${p.identity}`);
                updateRemoteStreams();
            });

            room.on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
                const speakingMap: Record<string, boolean> = {};
                speakers.forEach(s => {
                    speakingMap[s.identity] = true;
                });
                
                setParticipants(prev => prev.map(p => ({
                    ...p,
                    isSpeaking: !!speakingMap[p.username]
                })));

                const isLocalSpeaking = !!speakingMap[currentUser];
                socket.emit('voice-state-update', { isSpeaking: isLocalSpeaking });
            });

            addLog(`Connecting to LiveKit room...`);
            await room.connect(data.url, data.token);
            addLog(`Connected successfully to room: ${room.name}`);

            // Publish initial audio stream
            addLog(`Publishing audio track (muted: ${effectiveMuted})`);
            await room.localParticipant.setMicrophoneEnabled(!effectiveMuted);
            addLog(`Audio track successfully published.`);

        } catch (err: any) {
            addLog(`LiveKit error: ${err.name} - ${err.message}`);
            console.error('[LiveKit] Connection error:', err);
            const secureCheck = checkSecureContext();
            if (!secureCheck.isSecure) {
                setMicError(secureCheck.message);
            } else {
                setMicError(`Could not access microphone (${err.name}). Please check hardware/settings.`);
            }
        }
    };

    useEffect(() => {
        // Emit join immediately so presence list is populated right away
        socket.emit('join-voice-room', { communityId, channelId, username: currentUser });

        initLiveKit();

        socket.on('voice-participants-list', (list: VoiceParticipant[]) => {
            addLog(`Received voice-participants-list. Count: ${list.length}`);
            setParticipants(list);
            socket.emit('voice-state-update', {
                isMuted: isMutedRef.current || isDeafenedRef.current,
                isDeafened: isDeafenedRef.current,
                isCameraOn: isCameraOnRef.current,
                isScreenSharing: isScreenSharingRef.current
            });
        });

        socket.on('user-joined-voice', ({ username, socketId }: { username: string, socketId: string }) => {
            addLog(`Socket User joined voice: ${username}`);
            setParticipants(prev => {
                const existing = prev.find(p => p.username === username);
                if (existing) {
                    return prev.map(p => p.username === username ? { ...p, socketId, isMuted: false, isDeafened: false, isSpeaking: false } : p);
                }
                return [...prev, { username, socketId, isMuted: false, isDeafened: false, isSpeaking: false }];
            });
        });

        socket.on('user-left-voice', ({ username, socketId }: { username: string, socketId: string }) => {
            addLog(`Socket User left voice: ${username}`);
            setParticipants(prev => prev.filter(p => p.socketId !== socketId));
        });

        socket.on('voice-state-update', ({ socketId, state }: { socketId: string, state: any }) => {
            setParticipants(prev => prev.map(p => p.socketId === socketId ? { ...p, ...state } : p));
        });

        // Track visibility to log & handle background drops
        const handleVisibilityChange = () => {
            if (document.hidden) {
                addLog('App entered BACKGROUND.');
            } else {
                addLog('App entered FOREGROUND.');
                if (roomRef.current) {
                    addLog(`Connection state is: ${roomRef.current.state}`);
                    if (roomRef.current.state === 'disconnected') {
                        addLog('Connection lost. Reconnecting...');
                        initLiveKit();
                    }
                }
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            addLog('Leaving voice room and cleaning up...');
            socket.emit('leave-voice-room');
            socket.off('voice-participants-list');
            socket.off('user-joined-voice');
            socket.off('user-left-voice');
            socket.off('voice-state-update');
            document.removeEventListener('visibilitychange', handleVisibilityChange);

            if (roomRef.current) {
                roomRef.current.disconnect();
                roomRef.current = null;
            }
        };
    }, [communityId, channelId, currentUser]);

    // Update microphone mute status dynamically
    useEffect(() => {
        if (roomRef.current && roomRef.current.state === 'connected') {
            addLog(`Updating microphone mute status to: ${effectiveMuted}`);
            roomRef.current.localParticipant.setMicrophoneEnabled(!effectiveMuted).catch(err => {
                addLog(`Failed to toggle microphone: ${err.message}`);
            });
        }
        socket.emit('voice-state-update', { isMuted: effectiveMuted, isDeafened: isDeafened });
        setParticipants(prev => prev.map(p => p.username === currentUser ? { ...p, isMuted: effectiveMuted, isDeafened: isDeafened } : p));
    }, [effectiveMuted, isDeafened, currentUser]);

    const toggleCamera = async () => {
        try {
            const targetState = !isCameraOn;
            addLog(`Toggling camera to: ${targetState}`);
            if (roomRef.current && roomRef.current.state === 'connected') {
                await roomRef.current.localParticipant.setCameraEnabled(targetState, {
                    facingMode: facingMode
                });
                
                // Get local stream from LiveKit local participant tracks
                const tracks = Array.from(roomRef.current.localParticipant.trackPublications.values())
                    .map(pub => pub.track)
                    .filter(t => t && t.mediaStreamTrack);
                if (tracks.length > 0) {
                    const stream = new MediaStream();
                    tracks.forEach(t => {
                        if (t?.mediaStreamTrack) stream.addTrack(t.mediaStreamTrack);
                    });
                    setLocalStream(stream);
                } else {
                    setLocalStream(null);
                }
            } else {
                console.warn('[LiveKit] Room not connected. Toggling camera in local preview mode.');
                if (targetState) {
                    const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode } });
                    const videoTrack = stream.getVideoTracks()[0];
                    let nextStream = localStream;
                    if (!nextStream) {
                        nextStream = new MediaStream();
                    } else {
                        nextStream.getVideoTracks().forEach(t => { t.stop(); nextStream?.removeTrack(t); });
                    }
                    nextStream.addTrack(videoTrack);
                    setLocalStream(new MediaStream(nextStream.getTracks()));
                } else {
                    if (localStream) {
                        localStream.getVideoTracks().forEach(t => { t.stop(); localStream.removeTrack(t); });
                        setLocalStream(new MediaStream(localStream.getTracks()));
                    }
                }
            }
            setIsCameraOn(targetState);
            setIsScreenSharing(false);
            socket.emit('voice-state-update', { isCameraOn: targetState, isScreenSharing: false });
        } catch (err: any) {
            addLog(`Camera error: ${err.message}`);
            console.error('[LiveKit] Camera toggle error:', err);
        }
    };

    // Update camera facingMode
    useEffect(() => {
        const updateFacing = async () => {
            if (roomRef.current && roomRef.current.state === 'connected' && roomRef.current.localParticipant.isCameraEnabled) {
                await roomRef.current.localParticipant.setCameraEnabled(true, {
                    facingMode: facingMode
                });
                
                // Get local stream from LiveKit local participant tracks
                const tracks = Array.from(roomRef.current.localParticipant.trackPublications.values())
                    .map(pub => pub.track)
                    .filter(t => t && t.mediaStreamTrack);
                if (tracks.length > 0) {
                    const stream = new MediaStream();
                    tracks.forEach(t => {
                        if (t?.mediaStreamTrack) stream.addTrack(t.mediaStreamTrack);
                    });
                    setLocalStream(stream);
                }
            } else if (isCameraOn) {
                try {
                    const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode } });
                    const videoTrack = stream.getVideoTracks()[0];
                    let nextStream = localStream;
                    if (!nextStream) {
                        nextStream = new MediaStream();
                    } else {
                        nextStream.getVideoTracks().forEach(t => { t.stop(); nextStream?.removeTrack(t); });
                    }
                    nextStream.addTrack(videoTrack);
                    setLocalStream(new MediaStream(nextStream.getTracks()));
                } catch (err) {
                    console.error('[LiveKit] Fallback camera flip error:', err);
                }
            }
        };
        updateFacing();
    }, [facingMode]);

    const toggleScreenShare = async () => {
        try {
            const targetState = !isScreenSharing;
            addLog(`Toggling screen sharing to: ${targetState}`);
            if (roomRef.current && roomRef.current.state === 'connected') {
                await roomRef.current.localParticipant.setScreenShareEnabled(targetState);
                
                const tracks = Array.from(roomRef.current.localParticipant.trackPublications.values())
                    .map(pub => pub.track)
                    .filter(t => t && t.mediaStreamTrack);
                if (tracks.length > 0) {
                    const stream = new MediaStream();
                    tracks.forEach(t => {
                        if (t?.mediaStreamTrack) stream.addTrack(t.mediaStreamTrack);
                    });
                    setLocalStream(stream);
                } else {
                    setLocalStream(null);
                }
            } else {
                console.warn('[LiveKit] Room not connected. Toggling screen share in local preview mode.');
                if (targetState) {
                    const stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
                    const screenTrack = stream.getVideoTracks()[0];
                    let nextStream = localStream;
                    if (!nextStream) {
                        nextStream = new MediaStream();
                    } else {
                        nextStream.getVideoTracks().forEach(t => { t.stop(); nextStream?.removeTrack(t); });
                    }
                    nextStream.addTrack(screenTrack);
                    setLocalStream(new MediaStream(nextStream.getTracks()));
                    
                    screenTrack.onended = () => {
                        setIsScreenSharing(false);
                        socket.emit('voice-state-update', { isScreenSharing: false });
                        if (localStream) {
                            localStream.getVideoTracks().forEach(t => { t.stop(); localStream.removeTrack(t); });
                            setLocalStream(new MediaStream(localStream.getTracks()));
                        }
                    };
                } else {
                    if (localStream) {
                        localStream.getVideoTracks().forEach(t => { t.stop(); localStream.removeTrack(t); });
                        setLocalStream(new MediaStream(localStream.getTracks()));
                    }
                }
            }
            setIsScreenSharing(targetState);
            setIsCameraOn(false);
            socket.emit('voice-state-update', { isScreenSharing: targetState, isCameraOn: false });
        } catch (err: any) {
            addLog(`Screen share error: ${err.message}`);
            console.error('[LiveKit] Screen share toggle error:', err);
        }
    };

    const focusedOverlayNode = focusedParticipant && (
        <div 
            className="fixed inset-0 z-[150] bg-black/95 backdrop-blur-md flex flex-col items-center justify-center p-4 animate-fade-in"
            onClick={() => setFocusedParticipant(null)}
        >
            <div className="absolute top-4 right-4 z-[160]">
                <button 
                    type="button" 
                    onClick={() => setFocusedParticipant(null)}
                    className="p-3 bg-white/10 hover:bg-white/20 text-white rounded-full transition-all active:scale-95 shadow-lg"
                >
                    <CloseIcon className="w-6 h-6" />
                </button>
            </div>
            
            <div className={`w-full h-full flex items-center justify-center ${focusedParticipant.isScreenSharing ? 'max-w-full max-h-full' : 'max-w-2xl max-h-[70vh] rounded-[2rem] overflow-hidden border border-white/10 shadow-2xl bg-black/50'}`}>
                {(() => {
                    const participantStream = focusedParticipant.username === currentUser 
                        ? localStream 
                        : remoteStreamsMap[focusedParticipant.username.toLowerCase()];
                    return (
                        <RemoteStreamView 
                            stream={participantStream}
                            className="w-full h-full object-contain"
                        />
                    );
                })()}
            </div>
            
            <div className="absolute bottom-6 px-4 py-2 rounded-full bg-black/60 border border-white/10 backdrop-blur-sm text-white text-sm font-bold shadow-lg">
                {focusedParticipant.isScreenSharing ? `📺 Screen Share: ${focusedParticipant.username}` : `📷 Camera: ${focusedParticipant.username}`}
            </div>
        </div>
    );

    const participantsDest = document.getElementById('voice-participants-destination');
    const controlsDest = document.getElementById('voice-controls-destination');

    const participantsNode = (
        <div className="flex items-center gap-2 overflow-x-auto p-2 scrollbar-hide">
            {participants.map(p => {
                const userData = usersData[p.username] || { displayName: p.username, pfp: '' };
                const activeThemeColor = (userData as any).appThemeColor || themeColor;
                const hasVideo = !!(p.isCameraOn || p.isScreenSharing);
                const participantStream = p.username === currentUser ? localStream : remoteStreamsMap[p.username.toLowerCase()];

                return (
                    <div key={p.socketId} className="relative flex-shrink-0">
                        <ParticipantCard
                            p={p}
                            userData={userData}
                            activeThemeColor={activeThemeColor}
                            hasVideo={hasVideo}
                            participantStream={participantStream}
                            currentUser={currentUser}
                            isDeafened={isDeafened}
                            small={true}
                            onClick={() => setFocusedParticipant(p)}
                        />
                    </div>
                );
            })}
        </div>
    );

    const mainAreaNode = (
        <div className="flex-1 overflow-y-auto p-6">
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-x-4 gap-y-10">
                {participants.map(p => {
                    const userData = usersData[p.username] || { displayName: p.username, pfp: '' };
                    const activeThemeColor = (userData as any).appThemeColor || themeColor;
                    const hasVideo = !!(p.isCameraOn || p.isScreenSharing);
                    const participantStream = p.username === currentUser ? localStream : remoteStreamsMap[p.username.toLowerCase()];

                    return (
                        <ParticipantCard
                            key={p.socketId}
                            p={p}
                            userData={userData}
                            activeThemeColor={activeThemeColor}
                            hasVideo={hasVideo}
                            participantStream={participantStream}
                            currentUser={currentUser}
                            isDeafened={isDeafened}
                            onClick={() => setFocusedParticipant(p)}
                        />
                    );
                })}
            </div>
        </div>
    );

    const controlsNode = (
        <div className="w-full overflow-x-hidden">
            <div className="max-w-2xl mx-auto flex flex-wrap items-center justify-center gap-3 sm:gap-6">
                <button
                    onClick={() => setIsMuted(!isMuted)}
                    className={`p-4 text-raised rounded-full transition-all duration-300 ${isMuted ? 'bg-red-500 text-white' : 'bg-white/5 text-gray-300 hover:bg-white/10'}`}
                    title={isMuted ? "Unmute Mic" : "Mute Mic"}
                >
                    {isMuted ? <VolumeOffIcon className="w-6 h-6" /> : <MicIcon className="w-6 h-6" />}
                </button>

                <button
                    onClick={() => {
                        const nextDeafen = !isDeafened;
                        setIsDeafened(nextDeafen);
                        if (nextDeafen) {
                            setIsMuted(true);
                        }
                    }}
                    className={`p-4 text-raised rounded-full transition-all duration-300 ${isDeafened ? 'bg-red-500 text-white' : 'bg-white/5 text-gray-300 hover:bg-white/10'}`}
                    title={isDeafened ? "Undeafen Audio" : "Deafen Audio"}
                >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 18v-6a9 9 0 0118 0v6M4 19a2 2 0 002-2v-3a2 2 0 00-2-2H3v7h1zm16 0h1v-7h-1a2 2 0 00-2 2v3a2 2 0 002 2z" />
                        {isDeafened && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3l18 18" />}
                    </svg>
                </button>

                <button
                    onClick={toggleCamera}
                    className={`p-4 text-raised rounded-full transition-all duration-300 ${isCameraOn ? 'bg-blue-500 text-white' : 'bg-white/5 text-gray-300 hover:bg-white/10'}`}
                >
                    <CameraIcon className="w-6 h-6" />
                </button>

                {isCameraOn && (
                    <button
                        onClick={() => setFacingMode(prev => prev === 'user' ? 'environment' : 'user')}
                        className="p-4 text-raised bg-white/5 text-gray-300 hover:bg-white/10 rounded-full transition-all duration-300"
                    >
                        <FlipCameraIcon className="w-6 h-6" />
                    </button>
                )}

                <button
                    onClick={toggleScreenShare}
                    className={`p-4 rounded-full transition-all duration-300 ${isScreenSharing ? 'bg-green-500 text-white' : 'bg-white/5 text-gray-300 hover:bg-white/10'}`}
                >
                    <ScreenShareIcon className="w-6 h-6" />
                </button>

                <button
                    onClick={() => onLeave ? onLeave() : window.location.reload()}
                    className="p-4 text-raised bg-red-500 hover:bg-red-600 text-white rounded-full transition-all duration-300"
                >
                    <CloseIcon className="w-6 h-6" />
                </button>
            </div>
        </div>
    );

    const diagnosticsNode = (
        <DiagnosticsPanel
            logs={logs}
            room={roomRef.current}
            participants={participants}
            remoteStreamsMap={remoteStreamsMap}
            currentUser={currentUser}
            micError={micError}
            isMuted={isMuted}
            isDeafened={isDeafened}
            onReconnect={() => {
                addLog('User requested manual reconnect.');
                if (roomRef.current) {
                    roomRef.current.disconnect();
                    roomRef.current = null;
                }
                initLiveKit();
            }}
        />
    );

    return (
        <div className="flex flex-col h-full rounded-2xl m-4 overflow-hidden">
            {/* Visual Mic Error Banner at top of room */}
            {micError && (
                <div className="mx-4 mt-4 p-4 bg-red-500/20 border border-red-500/30 rounded-2xl text-red-200 text-sm font-medium animate-fade-in flex flex-col gap-1.5">
                    <span className="font-bold">⚠️ Microphone / Connection Error</span>
                    <span>{micError}</span>
                </div>
            )}

            {participantsDest ? createPortal(participantsNode, participantsDest) : mainAreaNode}
            <div className=" p-6">
                {controlsDest ? createPortal(controlsNode, controlsDest) : controlsNode}
            </div>
            {focusedOverlayNode}

            {/* Hidden persistent audio elements at root level */}
            <div style={{ display: 'none' }}>
                {participants.map(p => {
                    if (p.username === currentUser) return null;
                    const participantStream = remoteStreamsMap[p.username.toLowerCase()];
                    if (!participantStream) return null;
                    return (
                        <RemoteAudioPlayer 
                            key={`audio-${p.username}`} 
                            stream={participantStream} 
                            muted={isDeafened} 
                        />
                    );
                })}
            </div>

            {/* Floating Diagnostics widget */}
            {diagnosticsNode}
        </div>
    );
};

const ParticipantCard = React.memo(({ p, userData, activeThemeColor, hasVideo, participantStream, currentUser, isDeafened, small, onClick }: {
    p: VoiceParticipant;
    userData: any;
    activeThemeColor: string;
    hasVideo: boolean;
    participantStream: MediaStream | null;
    currentUser: string;
    isDeafened: boolean;
    small?: boolean;
    onClick?: () => void;
}) => {
    return (
        <div className={`flex flex-col items-center gap-2 animate-fade-in group ${small ? 'w-auto' : ''}`}>
            <div
                onClick={hasVideo ? onClick : undefined}
                className={`relative ${small ? 'w-10 h-10 border-2' : 'w-24 h-24 border-4'} rounded-full overflow-hidden transition-all duration-300 transform group-hover:scale-[1.05] ${p.isSpeaking ? 'scale-110' : 'scale-100'} ${hasVideo ? 'cursor-pointer' : ''}`}
                style={{ 
                    borderColor: p.isSpeaking ? activeThemeColor : 'rgba(255,255,255,0.1)',
                    boxShadow: p.isSpeaking ? `0 0 20px ${activeThemeColor}44` : 'none',
                    backgroundColor: '#111827',
                    isolation: 'isolate',
                    WebkitMaskImage: '-webkit-radial-gradient(white, black)'
                }}
            >
                {hasVideo && participantStream ? (
                    <RemoteStreamView
                        stream={participantStream}
                        isLocal={p.username === currentUser}
                        className="w-full h-full object-cover rounded-full"
                        style={{
                            WebkitMaskImage: '-webkit-radial-gradient(white, black)'
                        }}
                    />
                ) : (
                    userData.pfp ? (
                        <img
                            src={userData.pfp}
                            alt={p.username}
                            className="w-full h-full object-cover"
                        />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900 text-white text-3xl font-bold">
                            {p.username[0]?.toUpperCase()}
                        </div>
                    )
                )}

                {/* Status Indicators Overlay */}
                <div className="absolute inset-0 pointer-events-none ring-inset ring-1 ring-white/10 rounded-full" />
            </div>

            {!small && (
                <div className="flex flex-col items-center gap-1">
                    <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-black/40">
                        <span className="text-[10px] font-black text-white/90 truncate max-w-[80px] uppercase tracking-wider">
                            {userData.displayName || p.username}
                        </span>
                        {p.isDeafened && (
                            <svg className="w-2.5 h-2.5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M3 18v-6a9 9 0 0118 0v6M4 19a2 2 0 002-2v-3a2 2 0 00-2-2H3v7h1zm16 0h1v-7h-1a2 2 0 00-2 2v3a2 2 0 002 2z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M3 3l18 18" />
                            </svg>
                        )}
                        {p.isMuted && !p.isDeafened && <VolumeOffIcon className="w-2.5 h-2.5 text-red-500" />}
                    </div>
                </div>
            )}
            
            {small && (p.isMuted || p.isDeafened) && (
                <div className="absolute -bottom-1 -right-1 bg-black/80 rounded-full p-0.5 border border-white/10 z-10 flex gap-0.5">
                    {p.isDeafened ? (
                        <svg className="w-2.5 h-2.5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M3 18v-6a9 9 0 0118 0v6M4 19a2 2 0 002-2v-3a2 2 0 00-2-2H3v7h1zm16 0h1v-7h-1a2 2 0 00-2 2v3a2 2 0 002 2z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M3 3l18 18" />
                        </svg>
                    ) : (
                        <VolumeOffIcon className="w-2.5 h-2.5 text-red-500" />
                    )}
                </div>
            )}
        </div>
    );
});
ParticipantCard.displayName = 'ParticipantCard';

const DiagnosticsPanel = ({ logs, room, participants, remoteStreamsMap, currentUser, micError, isMuted, isDeafened, onReconnect }: {
    logs: string[];
    room: Room | null;
    participants: VoiceParticipant[];
    remoteStreamsMap: Record<string, MediaStream>;
    currentUser: string;
    micError: string | null;
    isMuted: boolean;
    isDeafened: boolean;
    onReconnect: () => void;
}) => {
    const [isOpen, setIsOpen] = useState(false);

    if (!isOpen) {
        return (
            <button
                onClick={() => setIsOpen(true)}
                className="fixed bottom-4 right-4 z-[200] px-4 py-2 bg-black/85 hover:bg-black text-white text-xs font-bold rounded-full border border-white/20 shadow-lg flex items-center gap-1.5 backdrop-blur-md transition-all active:scale-95"
            >
                <span>🎙️ Call Diagnostics</span>
            </button>
        );
    }

    const connectionState = room ? room.state : 'Not initialized';
    const localAudioPub = room?.localParticipant?.isMicrophoneEnabled ? 'Enabled' : 'Disabled';
    const localVideoPub = room?.localParticipant?.isCameraEnabled ? 'Enabled' : 'Disabled';

    return (
        <div className="fixed bottom-4 right-4 z-[200] w-80 max-h-[75vh] flex flex-col bg-black/95 text-white rounded-3xl border border-white/20 shadow-2xl overflow-hidden backdrop-blur-md">
            <div className="p-4 bg-white/5 border-b border-white/10 flex items-center justify-between">
                <span className="font-black text-xs uppercase tracking-wider text-blue-400">🎙️ Call Diagnostics</span>
                <button
                    onClick={() => setIsOpen(false)}
                    className="p-1 hover:bg-white/10 rounded-full text-gray-400 hover:text-white"
                >
                    <CloseIcon className="w-4 h-4" />
                </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4 text-xs">
                {/* Connection Info */}
                <div className="space-y-1 bg-white/5 p-3 rounded-2xl border border-white/5">
                    <div className="flex justify-between"><span className="text-gray-400">LiveKit Connection:</span> <span className="font-bold">{connectionState}</span></div>
                    <div className="flex justify-between"><span className="text-gray-400">Mic Publish:</span> <span>{localAudioPub}</span></div>
                    <div className="flex justify-between"><span className="text-gray-400">Camera Publish:</span> <span>{localVideoPub}</span></div>
                    <div className="flex justify-between"><span className="text-gray-400">Muted / Deafened:</span> <span>{isMuted ? 'Muted' : 'Unmuted'} / {isDeafened ? 'Deafened' : 'Undeafened'}</span></div>
                </div>

                {/* Mic Error Banner */}
                {micError && (
                    <div className="bg-red-500/20 border border-red-500/40 p-3 rounded-2xl text-red-300 font-medium">
                        ⚠️ {micError}
                    </div>
                )}

                {/* Participant List */}
                <div className="space-y-2">
                    <span className="font-bold text-[10px] text-gray-400 uppercase tracking-widest">Active Stream Matrix</span>
                    <div className="space-y-1 max-h-24 overflow-y-auto pr-1">
                        {participants.map(p => {
                            const isLocal = p.username === currentUser;
                            const remoteStream = isLocal ? null : remoteStreamsMap[p.username.toLowerCase()];
                            const tracksCount = remoteStream ? remoteStream.getTracks().length : 0;
                            return (
                                <div key={p.socketId} className="flex justify-between items-center py-1 border-b border-white/5 last:border-0">
                                    <span className="truncate max-w-[120px] font-medium">{p.username} {isLocal && '(You)'}</span>
                                    <span className="text-[10px] text-gray-400">
                                        {isLocal ? 'Local Stream' : `WebRTC Tracks: ${tracksCount}`}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Real-time Diagnostics Log */}
                <div className="space-y-2 flex-1 flex flex-col">
                    <span className="font-bold text-[10px] text-gray-400 uppercase tracking-widest">Live Room Events</span>
                    <div className="bg-black/50 p-2.5 rounded-2xl border border-white/5 font-mono text-[10px] max-h-36 overflow-y-auto text-gray-300 space-y-1">
                        {logs.length === 0 ? (
                            <div className="text-gray-500 italic">No events logged yet.</div>
                        ) : (
                            logs.map((log, idx) => (
                                <div key={idx} className="leading-relaxed break-words">{log}</div>
                            ))
                        )}
                    </div>
                </div>

                {/* Force Re-sync Button */}
                <button
                    onClick={onReconnect}
                    className="w-full py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl font-bold transition-all text-center"
                >
                    Force Reconnect
                </button>
            </div>
        </div>
    );
};
